<script setup lang="ts">
import { BaseCard } from '@/components/Cards'
import { TypographyItem } from '@/components/Typography'
import type { FFDragEvent } from '@/types/Firefox/DragEvent'
import { type DeviceModel } from '@/types/models/DeviceModel'
import { type Position } from '@vueuse/core'
import { ref } from 'vue'
import IconItem from '../../../../../components/Icon/IconItem.vue'
import StackLayout from '../../../../../components/Layouts/StackLayout.vue'
import { EDeviceStatus, EDeviceStatusColors } from '../../../../../types/enum/DeviceStatusEnum'

type Props = {
    device: Partial<DeviceModel>
}
const props = defineProps<Props>()
type Emits = {
    (e: 'dragStart', device: Partial<DeviceModel>): void
    (e: 'dragEnd', position?: Position): void
}
const emit = defineEmits<Emits>()

const handle = ref<{ cardRef: HTMLDivElement } | null>(null)

const mouseDown = (event: any) => {
    event.stopPropagation()

    const hideDragImage = event.currentTarget?.cloneNode(true)
    hideDragImage.id = 'hidedraggeimage'
    const dragImage = event.currentTarget?.cloneNode(true)
    dragImage.id = 'draggeimage'

    dragImage.style.width = 'fit-content'
    dragImage.style.position = 'absolute'
    dragImage.style.visibility = 'hidden'
    dragImage.style.transform = 'translate(-50%, -50%)'
    hideDragImage.style.opacity = 0

    document.body.appendChild(hideDragImage)
    document.body.appendChild(dragImage)

    event.dataTransfer?.setDragImage(hideDragImage, event.x, event.y)

    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
    else {
        event as TouchEvent
    }

    emit('dragStart', props.device)
}
const mouseUp = (event: FFDragEvent | TouchEvent) => {
    const dragImage = document.getElementById('draggeimage')
    const hideDragImage = document.getElementById('hidedraggeimage')
    if (dragImage && handle.value) {
        handle.value.cardRef.style.position = 'relative'
        handle.value.cardRef.style.removeProperty('visibility')
        handle.value.cardRef.style.removeProperty('left')
        handle.value.cardRef.style.removeProperty('top')
    }
    hideDragImage?.remove()
    dragImage?.remove()

    emit('dragEnd', {
        x:
            (event as FFDragEvent)._ffix_cx ??
            (event as FFDragEvent).x ??
            (event as TouchEvent).changedTouches?.[0]?.clientX,
        y:
            (event as FFDragEvent)._ffix_cy ??
            (event as FFDragEvent).y ??
            (event as TouchEvent).changedTouches?.[0]?.clientY
    })
}

const setPosition = (event: DragEvent | TouchEvent) => {
    const dragImage = document.getElementById('draggeimage')

    if (dragImage && handle.value) {
        dragImage.style.removeProperty('visibility')
        dragImage.style.left = `${
            (event as DragEvent).x ?? (event as TouchEvent).touches?.[0]?.clientX
        }px`
        dragImage.style.top = `${
            (event as DragEvent).y ?? (event as TouchEvent).touches?.[0]?.clientY
        }px`
        dragImage.style.pointerEvents = 'none'
        handle.value.cardRef.style.visibility = 'hidden'
    }
}
</script>

<template>
    <BaseCard
        ref="handle"
        :draggable="true"
        class="primary-border device-card"
        @dragstart="mouseDown"
        @dragend="mouseUp"
        @drag="setPosition"
        @touchstart="mouseDown"
        @touchend="mouseUp"
        @touchmove="setPosition">
        <template #content>
            <StackLayout class="space-between full-width">
                <TypographyItem v-if="device?.device_id || device?.id" :label="device.name" />
                <IconItem
                    icon="circle"
                    :type="
                        EDeviceStatusColors[
                            (device?.status as EDeviceStatus) ?? EDeviceStatus.OFFLINE
                        ]
                    " />
            </StackLayout>
        </template>
    </BaseCard>
</template>

<style scoped lang="scss">
.device-card {
    z-index: 2;
}
</style>
